import { useState } from 'react';
import { Badge, Caption1, Field, MessageBar, MessageBarBody, Spinner, Text, tokens } from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import type { DirectoryUserDto, ManagedDeviceDto } from '../api/types';
import './devices.css';

function complianceColor(c?: string): 'success' | 'danger' | 'warning' | 'subtle' {
  const v = (c ?? '').toLowerCase();
  if (v === 'compliant') return 'success';
  if (v === 'noncompliant') return 'danger';
  if (v === 'ingraceperiod' || v === 'conflict') return 'warning';
  return 'subtle';
}

function fmtDate(s?: string): string {
  return s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

export function DevicesInventoryPage() {
  const api = useApi();
  const [selected, setSelected] = useState<DirectoryUserDto | undefined>();
  const [devices, setDevices] = useState<ManagedDeviceDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function pick(u: DirectoryUserDto | undefined) {
    setSelected(u);
    setDevices(null);
    setError(null);
    if (!u) return;
    setLoading(true);
    try {
      setDevices(await api.getUserDevices(u.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Geräte-Inventar"
        description="Intune-verwaltete Geräte eines Benutzers — Compliance, OS-Version und Patch-Stand."
      />
      <div style={{ maxWidth: 1000, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
        <Field label="Benutzer">
          <UserPicker onSelect={pick} />
        </Field>

        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}
        {loading ? <Spinner size="tiny" label="Lade Geräte…" /> : null}

        {selected && devices && devices.length === 0 ? (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
            Keine verwalteten Geräte gefunden (Intune-Lizenz/Enrollment vorausgesetzt).
          </Caption1>
        ) : null}

        {devices && devices.length > 0 ? (
          <div className="dev-tablewrap">
            <table className="dev-table">
              <thead>
                <tr>
                  <th>Gerät</th>
                  <th>OS</th>
                  <th>Version</th>
                  <th>Compliance</th>
                  <th>Besitz</th>
                  <th>Modell</th>
                  <th>Seriennr.</th>
                  <th>Letzte Sync</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Text weight="semibold" size={200}>
                        {d.deviceName}
                      </Text>
                    </td>
                    <td>{d.operatingSystem ?? '—'}</td>
                    <td>{d.osVersion ?? '—'}</td>
                    <td>
                      {d.compliance ? (
                        <Badge appearance="tint" color={complianceColor(d.compliance)}>
                          {d.compliance}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{d.ownership ?? '—'}</td>
                    <td>{d.model ?? '—'}</td>
                    <td>{d.serialNumber ?? '—'}</td>
                    <td>{fmtDate(d.lastSync)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
